import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { focusAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { FocusSession, FocusSessionCreate, FocusSessionUpdate, FocusSessionItemCreate } from '../types';

const FOCUS_QUERY_KEY = ['focus-session'];

/** Parse a server timestamp as UTC. Server returns naive datetimes (no timezone
 *  indicator), but they represent UTC. Without the 'Z' suffix, JavaScript's
 *  Date constructor interprets them as local time, causing timer drift equal
 *  to the user's UTC offset. */
export function parseUtcTimestamp(ts: string): number {
  if (!ts.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ts)) {
    return new Date(ts + 'Z').getTime();
  }
  return new Date(ts).getTime();
}

export function useFocusSession() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // ---------- Query: active session ----------
  const {
    data: session = null,
    isLoading,
  } = useQuery<FocusSession | null>({
    queryKey: FOCUS_QUERY_KEY,
    queryFn: focusAPI.getActive,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    enabled: !!user,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const s = query.state.data;
      // Poll more frequently when active/paused for timer accuracy
      if (s && (s.status === 'active' || s.status === 'paused')) return 30_000;
      return false;
    },
  });

  // ---------- Timer: client-side countdown ----------
  const [now, setNow] = useState(() => Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const sessionStatus = session?.status;

  useEffect(() => {
    if (sessionStatus === 'active' || sessionStatus === 'paused') {
      timerRef.current = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(timerRef.current);
    }
    if (timerRef.current) clearInterval(timerRef.current);
  }, [sessionStatus]);

  // Re-sync on visibility change
  useEffect(() => {
    const handler = () => {
      if (!document.hidden) {
        setNow(Date.now());
        queryClient.invalidateQueries({ queryKey: FOCUS_QUERY_KEY });
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [queryClient]);

  const timerInfo = useMemo(() => {
    if (!session || !session.started_time || !session.planned_duration_minutes) {
      return { remainingSeconds: null, pauseElapsedSeconds: 0, isOvertime: false };
    }

    const startedMs = parseUtcTimestamp(session.started_time);
    const plannedMs = session.planned_duration_minutes * 60 * 1000;
    const totalPauseMs = session.total_pause_seconds * 1000;

    let elapsedMs: number;
    let pauseElapsedSeconds = 0;

    if (session.status === 'paused' && session.pause_started_time) {
      // When paused, freeze main timer at pause start
      const pauseStartMs = parseUtcTimestamp(session.pause_started_time);
      elapsedMs = pauseStartMs - startedMs - totalPauseMs;
      pauseElapsedSeconds = Math.floor((now - pauseStartMs) / 1000);
    } else if (session.status === 'active') {
      elapsedMs = now - startedMs - totalPauseMs;
    } else {
      // completed/abandoned — show final state
      const endMs = session.completed_time ? parseUtcTimestamp(session.completed_time) : now;
      elapsedMs = endMs - startedMs - totalPauseMs;
    }

    const remainingSeconds = Math.floor((plannedMs - elapsedMs) / 1000);
    return {
      remainingSeconds,
      pauseElapsedSeconds,
      isOvertime: remainingSeconds < 0,
    };
  }, [session, now]);

  // ---------- Notepad debounced save ----------
  const [localNotepad, setLocalNotepad] = useState('');
  const notepadTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const notepadDirtyRef = useRef(false);

  // Sync local notepad when session changes — skip if user has unsaved edits
  const sessionNotepad = session?.notepad;
  useEffect(() => {
    if (sessionNotepad !== undefined && !notepadDirtyRef.current) {
      setLocalNotepad(sessionNotepad);
    }
  }, [sessionNotepad]);

  // Cleanup pending notepad save on unmount
  useEffect(() => {
    return () => {
      if (notepadTimerRef.current) clearTimeout(notepadTimerRef.current);
    };
  }, []);

  const sessionId = session?.id;
  const updateNotepad = useCallback((text: string) => {
    setLocalNotepad(text);
    notepadDirtyRef.current = true;
    if (notepadTimerRef.current) clearTimeout(notepadTimerRef.current);
    if (!sessionId) return;
    notepadTimerRef.current = setTimeout(() => {
      focusAPI.update(sessionId, { notepad: text }).then(() => {
        notepadDirtyRef.current = false;
        queryClient.invalidateQueries({ queryKey: FOCUS_QUERY_KEY });
      }).catch((err) => {
        notepadDirtyRef.current = false;
        console.error('Failed to save notepad:', err);
      });
    }, 2000);
  }, [sessionId, queryClient]);

  // ---------- Mutations ----------
  const [mutationError, setMutationError] = useState<string | null>(null);

  const invalidate = useCallback(() => {
    setMutationError(null);
    queryClient.invalidateQueries({ queryKey: FOCUS_QUERY_KEY });
  }, [queryClient]);

  const invalidateWithTodos = useCallback(() => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: ['todos'] });
  }, [invalidate, queryClient]);

  const onMutationError = useCallback((err: Error) => {
    const message = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      || err.message || 'Something went wrong';
    setMutationError(message);
  }, []);

  const createSession = useMutation({
    mutationFn: (data: FocusSessionCreate) => focusAPI.create(data),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const updateSession = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FocusSessionUpdate }) => focusAPI.update(id, data),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const addItem = useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: FocusSessionItemCreate }) =>
      focusAPI.addItem(sessionId, data),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const removeItem = useMutation({
    mutationFn: ({ sessionId, itemId }: { sessionId: string; itemId: string }) =>
      focusAPI.removeItem(sessionId, itemId),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const reorderItems = useMutation({
    mutationFn: ({ sessionId, itemIds }: { sessionId: string; itemIds: string[] }) =>
      focusAPI.reorderItems(sessionId, itemIds),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const completeItem = useMutation({
    mutationFn: ({ sessionId, itemId }: { sessionId: string; itemId: string }) =>
      focusAPI.completeItem(sessionId, itemId),
    onSuccess: invalidateWithTodos,
    onError: onMutationError,
  });

  const skipItem = useMutation({
    mutationFn: ({ sessionId, itemId }: { sessionId: string; itemId: string }) =>
      focusAPI.skipItem(sessionId, itemId),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const uncompleteItem = useMutation({
    mutationFn: ({ sessionId, itemId }: { sessionId: string; itemId: string }) =>
      focusAPI.uncompleteItem(sessionId, itemId),
    onSuccess: invalidateWithTodos,
    onError: onMutationError,
  });

  const unskipItem = useMutation({
    mutationFn: ({ sessionId, itemId }: { sessionId: string; itemId: string }) =>
      focusAPI.unskipItem(sessionId, itemId),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const startSession = useMutation({
    mutationFn: (id: string) => focusAPI.start(id),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const pauseSession = useMutation({
    mutationFn: (id: string) => focusAPI.pause(id),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const resumeSession = useMutation({
    mutationFn: (id: string) => focusAPI.resume(id),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const completeSession = useMutation({
    mutationFn: (id: string) => focusAPI.complete(id),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const abandonSession = useMutation({
    mutationFn: (id: string) => focusAPI.abandon(id),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  // ---------- Current item (first non-completed, non-skipped) ----------
  const currentItem = useMemo(() => {
    if (!session) return null;
    return session.items.find(i => !i.is_completed && !i.is_skipped) ?? null;
  }, [session]);

  const clearError = useCallback(() => setMutationError(null), []);

  return {
    session,
    isLoading,
    ...timerInfo,
    localNotepad,
    updateNotepad,
    currentItem,
    mutationError,
    clearError,
    // Mutations
    createSession,
    updateSession,
    addItem,
    removeItem,
    reorderItems,
    completeItem,
    skipItem,
    uncompleteItem,
    unskipItem,
    startSession,
    pauseSession,
    resumeSession,
    completeSession,
    abandonSession,
  };
}
